import { Entity } from '@backstage/catalog-model';
import { CatalogApi } from '@backstage/plugin-catalog-react';

export type CatalogVcsProvider = 'github' | 'bitbucket' | 'unknown';

export function getEntityVcsProvider(entity: Entity): CatalogVcsProvider {
  const slug = entity.metadata.annotations?.['circleci.com/project-slug'] ?? '';
  if (slug.startsWith('github/')) {
    return 'github';
  }
  if (slug.startsWith('bitbucket/')) {
    return 'bitbucket';
  }

  const location =
    entity.metadata.annotations?.['backstage.io/managed-by-location'] ??
    entity.metadata.annotations?.['backstage.io/managed-by-origin-location'] ??
    '';
  if (location.includes('github.com')) {
    return 'github';
  }
  if (location.includes('bitbucket.org')) {
    return 'bitbucket';
  }

  return 'unknown';
}

const CATALOG_UNIQUENESS_ERROR = 'already exists in the catalog';
const GITHUB_ORG_UNIQUENESS_ERROR = 'already exists in the GitHub organization';

export function githubRepoTakenMessage(repoName: string, org: string): string {
  return `Repository name "${repoName}" ${GITHUB_ORG_UNIQUENESS_ERROR} "${org}". Choose a different name.`;
}

/**
 * Extract the org login from a GitHubProjectPickerField value, which is a
 * catalog-style ref such as `resource:default/ghe-carrier-digital`. A plain
 * org login is returned as-is.
 */
export function githubOrgFromFieldValue(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }
  const name = value.trim().split('/').pop()!.split(':').pop()!;
  return name.startsWith('ghe-') ? name.slice('ghe-'.length) : name;
}

export function catalogNameTakenMessage(
  repoName: string,
  provider: CatalogVcsProvider,
): string {
  if (provider === 'github') {
    return `Repository name "${repoName}" already exists in the catalog on GitHub. Choose a different name.`;
  }
  if (provider === 'bitbucket') {
    return `Repository name "${repoName}" already exists in the catalog on Bitbucket. Choose a different name.`;
  }
  return `Repository name "${repoName}" already exists in the catalog. Choose a different name.`;
}

/** True for name-taken errors (catalog or GitHub org), which the name field shows inline. */
export function isCatalogUniquenessError(error: unknown): boolean {
  const matches = (text: string) =>
    text.includes(CATALOG_UNIQUENESS_ERROR) ||
    text.includes(GITHUB_ORG_UNIQUENESS_ERROR);
  if (typeof error === 'string') {
    return matches(error);
  }
  if (error && typeof error === 'object') {
    const maybe = error as { message?: string; stack?: string };
    return matches(maybe.message ?? '') || matches(maybe.stack ?? '');
  }
  return false;
}

export async function findCatalogComponentByRepositoryName(
  catalogApi: CatalogApi,
  repositoryName: string,
): Promise<Entity | undefined> {
  const name = repositoryName.trim().toLowerCase();
  if (!name) {
    return undefined;
  }

  return catalogApi.getEntityByRef({
    kind: 'Component',
    namespace: 'default',
    name,
  });
}
